import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { FEATURE_MIN_TIER } from "../../lib/tier-config";
import { TierBadge } from "./TierBadge";
import { cn } from "../../lib/utils";

const TIER_LABEL = { free: "Free", lite: "Lite", pro: "Pro" };

/**
 * UpgradePrompt — inline CTA shown when a feature is gated.
 *
 * Resolves the required tier from FEATURE_MIN_TIER (single source of truth) so it
 * can never drift from enforcement, and deep-links to /pricing?feature=<id> so the
 * pricing page can highlight the relevant row.
 *
 * @param {{
 *   feature: string,
 *   tier?: "lite"|"pro",      // override the resolved required tier
 *   message?: string,
 *   className?: string,
 *   compact?: boolean,
 * }} props
 */
export function UpgradePrompt({ feature, tier, message, className, compact = false }) {
  const requiredTier = tier || FEATURE_MIN_TIER[feature] || "pro";
  const tierLabel = TIER_LABEL[requiredTier] || "Pro";
  const href = `/pricing?feature=${encodeURIComponent(feature || "")}`;
  const copy =
    message || `Upgrade to ${tierLabel} to unlock this. One organizer pays — the whole table benefits.`;

  return (
    <div
      data-testid="upgrade-prompt"
      className={cn(
        "flex items-start gap-3 rounded-2xl border border-amber-200/70 bg-amber-50/70 p-3 text-sm dark:border-amber-700/40 dark:bg-amber-900/20",
        compact && "p-2",
        className
      )}
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <TierBadge tier={requiredTier} />
          <span className="font-semibold text-slate-700 dark:text-slate-200">required</span>
        </div>
        {!compact && (
          <p className="text-xs text-slate-600 dark:text-slate-300">{copy}</p>
        )}
        <Link
          to={href}
          className="inline-flex w-fit items-center rounded-full bg-brand-primary px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-primary/90"
        >
          Upgrade to {tierLabel}
        </Link>
      </div>
    </div>
  );
}

export default UpgradePrompt;
